import { useEffect } from 'react';
import type { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';

import g from 'src/constants/urls';
import { IArticleByGroupDetail } from 'src/interface/ArticleByGroup';
import wrapper from 'src/redux';

import { decryptDLoginBE } from '@/utils/func/encrypt';
import { gtmTrackEvent } from '@/utils/gtmTrackEvent';

const HeadC = dynamic(() => import('src/components/Head'));
const ArticleContainer = dynamic(() => import('src/containers/Articles'));
// const ArticleMenusContainer = dynamic(() => import('src/containers/ArticlesMenus'));
// const ArticleDownloadContainer = dynamic(() => import('src/containers/Download/Articles'));

const Article = (props: any) => {
	const router = useRouter();

	useEffect(() => {
		if (!router.isReady) return;

		const from = router.query.from;
		const dlogin = Cookies.get('d_login');

		let userData = null;

		if (typeof dlogin === 'string') {
			userData = decryptDLoginBE(dlogin);
		}

		let formattedDate = null;
		if (userData?.birthdate) {
			const d = new Date(userData.birthdate);
			if (!isNaN(d.getTime())) {
			formattedDate = d.toISOString().split('T')[0];
			}
		}

		let eventName = '';
		if (from === 'login') {
			eventName = 'login_success';
		} else if (from === 'register') {
			eventName = 'register_success';
		}

		if (!eventName || !userData) return;

		setTimeout(() => {
			gtmTrackEvent({
				event: eventName,
				uid: userData.uid,
				email: userData.email,
				birthdate: formattedDate,
				name: userData.name,
				phone_number: userData.phone_number,
				weight: userData.weight,
				height: userData.height,
				referral: userData.referal_code,
				phase: userData.web_phase_type,
			});
		}, 500);

	}, [router.isReady, router.query.from]);

	return (
		<>
			<HeadC
				title="Artikel Kehamilan & Parenting Terlengkap untuk Mama"
				description={
					'Temukan artikel informatif seputar kehamilan, persiapan menjadi ibu,' +
					' dan parenting di HalloBumil.' +
					' Dapatkan tips dan panduan lengkap untuk bumil dan calon ibu.'
				}
			/>
			{/* <ArticleHeaderContainer /> */}
			{/* <ArticleMenusContainer {...props}/> */}
			<ArticleContainer {...props} />
			{/* <ArticleDownloadContainer /> */}
		</>
	);
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
	(store: any) => async () => {
		let isError: boolean = false;
		let artikelSec1: any = [];
		let artikelSec2: any = [];
		let artikelSec3: any = [];
		let artikelSecPH1: any = [];
		let artikelSecPH2: any = [];
		let artikelSecPH3: any = [];
		const queryClient = new QueryClient();

		try {
			const filterSectionBanner = 'order=desc&orderby=show_as_banner%2Cpublish_date%2Ctotal_like&per_page=4&page=1&show_as_banner=true';
			const uri = `${g.API_ARTICLE}/all?${filterSectionBanner}`;
			const opts = {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			};

			const s1 = await fetch(uri, opts);
			artikelSec1 = await s1.json().then((res) => ({ ...res, status: s1.status }));

			const uriS2 = `${g.API_ARTICLE}/all?order=desc&orderby=total_like&per_page=9&page=1`;
			const optsS2 = {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			};

			const s2 = await fetch(uriS2, optsS2);
			artikelSec2 = await s2.json().then((res) => ({ ...res, status: s2.status }));

			const uriS3 = `${g.API_ARTICLE}/all?order=desc&orderby=publish_date&per_page=6&page=1`;
			const optsS3 = {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			};

			const s3 = await fetch(uriS3, optsS3);
			artikelSec3 = await s3.json().then((res) => ({ ...res, status: s3.status }));

			const uriPH1 = `${g.API_ARTICLE}/pre-pregnancy?order=desc&orderby=publish_date&per_page=6&page=1`;
			const optPH1 = {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			};

			const ph1 = await fetch(uriPH1, optPH1);
			artikelSecPH1 = await ph1.json().then((res) => ({ ...res, status: ph1.status }));

			const uriPH2 = `${g.API_ARTICLE}/pregnancy?order=desc&orderby=publish_date&per_page=6&page=1`;
			const optPH2 = {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			};

			const ph2 = await fetch(uriPH2, optPH2);
			artikelSecPH2 = await ph2.json().then((res) => ({ ...res, status: ph2.status }));

			const uriPH3 = `${g.API_ARTICLE}/post-pregnancy?order=desc&orderby=publish_date&per_page=6&page=1`;
			const optPH3 = {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			};

			const ph3 = await fetch(uriPH3, optPH3);
			artikelSecPH3 = await ph3.json().then((res) => ({ ...res, status: ph3.status }));
		} catch (err: any) {
			if (err?.status === 400) {
				isError = true;
			} else {
				throw err;
			}
		}
		const article: IArticleByGroupDetail[] =
			store.getState().articleByGroup.articleByCategory?.data?.records;

		if (!article && isError) return { notFound: true };
		return {
			props: {
				dehydratedState: dehydrate(queryClient),
				card: true,
				isWhiteBackground: false,
				JsonLd: {
					organization: true,
					breadcrumbs: true,
				},
				pageHeaderContent: {
					title: 'artikel semua topik',
					breadcrumbs: [
						{ label: 'Artikel ', navigatePath: '/artikel' },
						{ label: 'Semua Topik', navigatePath: '/artikel' },
					],
				},
				section1: artikelSec1.data.records || [],
				section2: artikelSec2.data.records || [],
				section3: artikelSec3.data.records || [],
				artikelSec3TotalRecord: artikelSec3.meta.pagination.total_records || '0',
				artikelSecPH1: artikelSecPH1.data.records || [],
				artikelSecPH2: artikelSecPH2.data.records || [],
				artikelSecPH3: artikelSecPH3.data.records || [],
				artikelSecPH1Records: artikelSecPH1.meta.pagination.total_records || '0',
				artikelSecPH2Records: artikelSecPH2.meta.pagination.total_records || '0',
				artikelSecPH3Records: artikelSecPH3.meta.pagination.total_records || '0',
			},
		};
	},
);

export default Article;
